import {
  CircleCheck,
  CircleHelp,
  CircleSlash,
  CircleX,
  Clock,
  ClockAlert,
  Info,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  TriangleAlert,
  Unplug,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "./Controls";

export type StatusCode =
  | "healthy"
  | "warning"
  | "error"
  | "unknown"
  | "offline"
  | "checking"
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "reconciling"
  | "active"
  | "expiring"
  | "expired"
  | "revoked";

const statusContract: Record<
  StatusCode,
  {
    readonly Icon: LucideIcon;
    readonly label: string;
    readonly tone: "success" | "warning" | "danger" | "info" | "neutral";
  }
> = {
  healthy: { Icon: CircleCheck, label: "Sağlıklı", tone: "success" },
  warning: { Icon: TriangleAlert, label: "Dikkat gerekiyor", tone: "warning" },
  error: { Icon: CircleX, label: "Hata", tone: "danger" },
  unknown: { Icon: CircleHelp, label: "Bilinmiyor", tone: "neutral" },
  offline: { Icon: Unplug, label: "Erişilemiyor", tone: "danger" },
  checking: { Icon: RefreshCw, label: "Kontrol ediliyor", tone: "info" },
  queued: { Icon: Clock, label: "Kabul edildi", tone: "neutral" },
  running: { Icon: LoaderCircle, label: "Çalışıyor", tone: "info" },
  succeeded: { Icon: CircleCheck, label: "Başarılı", tone: "success" },
  failed: { Icon: CircleX, label: "Başarısız", tone: "danger" },
  cancelled: { Icon: CircleSlash, label: "İptal edildi", tone: "neutral" },
  reconciling: { Icon: RefreshCw, label: "Durum doğrulanıyor", tone: "warning" },
  active: { Icon: KeyRound, label: "Etkin", tone: "success" },
  expiring: { Icon: ClockAlert, label: "Süresi yakında doluyor", tone: "warning" },
  expired: { Icon: Clock, label: "Süresi doldu", tone: "neutral" },
  revoked: { Icon: KeyRound, label: "İptal edildi", tone: "danger" },
};

export function StatusBadge({
  label,
  status,
}: {
  readonly label?: string;
  readonly status: StatusCode;
}) {
  const contract = statusContract[status];
  return (
    <span className={`status-badge status-badge--${contract.tone}`}>
      <contract.Icon aria-hidden="true" size={16} strokeWidth={1.75} />
      <span>{label ?? contract.label}</span>
    </span>
  );
}

export function AlertBanner({
  children,
  heading,
  tone = "info",
}: {
  readonly children: ReactNode;
  readonly heading: string;
  readonly tone?: "info" | "success" | "warning" | "error";
}) {
  const Icon =
    tone === "error"
      ? CircleX
      : tone === "warning"
        ? TriangleAlert
        : tone === "success"
          ? CircleCheck
          : Info;
  return (
    <section
      aria-live={tone === "error" ? "assertive" : "polite"}
      className={`alert alert--${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <div className="cluster">
        <Icon aria-hidden="true" size={20} strokeWidth={1.75} />
        <p className="alert__title">{heading}</p>
      </div>
      <div>{children}</div>
    </section>
  );
}

export function InlineResult({
  children,
  requestId,
  tone = "success",
}: {
  readonly children: ReactNode;
  readonly requestId?: string | undefined;
  readonly tone?: "success" | "error" | "info";
}) {
  return (
    <AlertBanner
      heading={
        tone === "success"
          ? "İşlem tamamlandı"
          : tone === "error"
            ? "İşlem tamamlanamadı"
            : "İşlem durumu"
      }
      tone={tone}
    >
      <p>{children}</p>
      {requestId === undefined ? null : <p className="technical">İstek kimliği: {requestId}</p>}
    </AlertBanner>
  );
}

export function Skeleton({ label = "Bilgiler yükleniyor" }: { readonly label?: string }) {
  return (
    <div aria-busy="true" aria-label={label} className="stack">
      <div aria-hidden="true" className="skeleton" />
      <div aria-hidden="true" className="skeleton" />
    </div>
  );
}

export function EmptyState({
  action,
  body,
  heading,
}: {
  readonly action?: ReactNode;
  readonly body: string;
  readonly heading: string;
}) {
  return (
    <section className="card">
      <h2>{heading}</h2>
      <p>{body}</p>
      {action}
    </section>
  );
}

export function ErrorState({
  onRetry,
  requestId,
}: {
  readonly onRetry?: (() => void) | undefined;
  readonly requestId?: string | undefined;
}) {
  return (
    <AlertBanner heading="Bilgiler yüklenemedi" tone="error">
      <p>
        Bağlantınızı kontrol edip yeniden deneyin.
        {requestId === undefined ? "" : ` Sorun sürerse istek kimliğini paylaşın: ${requestId}.`}
      </p>
      {onRetry === undefined ? null : (
        <Button onClick={onRetry} variant="secondary">
          Yeniden dene
        </Button>
      )}
    </AlertBanner>
  );
}

export function OfflineBanner({ updatedAt }: { readonly updatedAt?: string | undefined }) {
  return (
    <AlertBanner heading="Sunucuyla bağlantı kurulamadı" tone="warning">
      <p>
        Son doğrulanan bilgiler gösteriliyor.
        {updatedAt === undefined ? "" : ` Son güncelleme: ${updatedAt}.`}
      </p>
    </AlertBanner>
  );
}
